import { z } from "zod";

// =============================================================================
// Asset Type Enum
// =============================================================================

export const assetTypeSchema = z.enum([
  "stock",
  "crypto",
  "fiat",
  "index",
  "etf",
  "fund",
]);

export type AssetType = z.infer<typeof assetTypeSchema>;

// =============================================================================
// ETF-specific schemas
// =============================================================================

export const etfHoldingSchema = z.object({
  symbol: z.string(),
  name: z.string(),
  holding_percent: z.number(),
});

export const etfAssetClassesSchema = z.object({
  stock_position: z.number(),
  bond_position: z.number(),
  cash_position: z.number(),
  preferred_position: z.number().optional(),
  convertible_position: z.number().optional(),
  other_position: z.number().optional(),
});

export const fundHoldingMetricSchema = z.object({
  value: z.number().optional(),
  category_average: z.number().optional(),
});

export type ETFHolding = z.infer<typeof etfHoldingSchema>;
export type ETFAssetClasses = z.infer<typeof etfAssetClassesSchema>;

// =============================================================================
// Enriched Asset Fields (for stocks and ETFs)
// =============================================================================

export const enrichedAssetFieldsSchema = z.object({
  // Company info
  sector: z.string().optional(),
  industry: z.string().optional(),
  country: z.string().optional(),
  website: z.string().optional(),
  description: z.string().optional(),
  full_time_employees: z.number().optional(),

  // Valuation metrics
  market_cap: z.number().optional(),
  market_cap_usd: z.number().optional(),
  enterprise_value: z.number().optional(),
  trailing_pe: z.number().optional(),
  forward_pe: z.number().optional(),
  peg_ratio: z.number().optional(),
  price_to_book: z.number().optional(),
  price_to_sales: z.number().optional(),

  // Dividend info
  dividend_yield: z.number().optional(),
  dividend_rate: z.number().optional(),
  payout_ratio: z.number().optional(),

  // Technical indicators
  beta: z.number().optional(),
  fifty_two_week_high: z.number().optional(),
  fifty_two_week_low: z.number().optional(),
  fifty_day_average: z.number().optional(),
  two_hundred_day_average: z.number().optional(),
  all_time_high: z.number().optional(),
  all_time_low: z.number().optional(),

  // Real-time price data
  previous_close: z.number().optional(),
  previous_close_at: z.string().optional(),
  regular_market_open: z.number().optional(),
  regular_market_price: z.number().optional(),
  regular_market_day_high: z.number().optional(),
  regular_market_day_low: z.number().optional(),
  latest_price: z.number().optional(),
  daily_change_percent: z.number().optional(),

  // Share data
  average_volume: z.number().optional(),
  shares_outstanding: z.number().optional(),
  float_shares: z.number().optional(),
  held_percent_insiders: z.number().optional(),
  held_percent_institutions: z.number().optional(),
  short_ratio: z.number().optional(),

  // Growth metrics
  book_value: z.number().optional(),
  earnings_quarterly_growth: z.number().optional(),
  revenue_growth: z.number().optional(),

  // Margins
  gross_margins: z.number().optional(),
  operating_margins: z.number().optional(),
  profit_margins: z.number().optional(),

  // Returns
  return_on_assets: z.number().optional(),
  return_on_equity: z.number().optional(),

  // Cash/Debt
  total_cash: z.number().optional(),
  total_debt: z.number().optional(),
  current_ratio: z.number().optional(),
  quick_ratio: z.number().optional(),
  free_cashflow: z.number().optional(),
  operating_cashflow: z.number().optional(),

  // ETF-specific fields
  total_assets: z.number().optional(),
  nav_price: z.number().optional(),
  category: z.string().optional(),
  net_expense_ratio: z.number().optional(),
  fund_family: z.string().optional(),
  fund_inception_date: z.string().optional(),
  ytd_return: z.number().optional(),
  three_year_avg_return: z.number().optional(),
  five_year_avg_return: z.number().optional(),
  beta_3year: z.number().optional(),

  // ETF Holdings & Allocation
  top_holdings: z.array(etfHoldingSchema).optional(),
  sector_weightings: z.record(z.string(), z.number()).optional(),
  asset_classes: etfAssetClassesSchema.optional(),

  last_enriched: z.string().optional(),
});

export type EnrichedAssetFields = z.infer<typeof enrichedAssetFieldsSchema>;

// =============================================================================
// Base Asset Schema
// =============================================================================

const assetCommonSchema = z.object({
  asset_id: z.string(),
  name: z.string(),
  symbol: z.string(),
  type: assetTypeSchema,
  last_sync: z.string(),
  is_primary: z.boolean().optional(),
  latest_price: z.number().optional(),
  daily_change_percent: z.number().optional(),
});

// =============================================================================
// Asset Type-specific Schemas
// =============================================================================

export const stockAssetSchema = assetCommonSchema
  .merge(enrichedAssetFieldsSchema)
  .extend({
    type: z.literal("stock"),
    isin: z.string(),
    exchange: z.string(),
    currency: z.string(),
    currency_symbol: z.string().optional(),
  });

export const etfAssetSchema = assetCommonSchema
  .merge(enrichedAssetFieldsSchema)
  .extend({
    type: z.literal("etf"),
    isin: z.string(),
    exchange: z.string(),
    currency: z.string(),
    currency_symbol: z.string().optional(),
  });

export const fundAssetSchema = assetCommonSchema
  .merge(enrichedAssetFieldsSchema)
  .extend({
    type: z.literal("fund"),
    currency: z.string(),
    currency_symbol: z.string().optional(),
    isin: z.string().optional(),
    exchange: z.string().optional(),
    legal_type: z.string().optional(),
    net_assets: z.number().optional(),
    expense_ratio: z.number().optional(),
    turnover: z.number().optional(),
    yield: z.number().optional(),
    equity_holdings: z.record(z.string(), fundHoldingMetricSchema).optional(),
    bond_holdings: z.record(z.string(), fundHoldingMetricSchema).optional(),
    bond_ratings: z.record(z.string(), z.number()).optional(),
  });

export const cryptoAssetSchema = assetCommonSchema.extend({
  type: z.literal("crypto"),
  currency: z.string(),
  currency_symbol: z.string().optional(),
  // Crypto-specific enriched fields
  market_cap: z.number().optional(),
  market_cap_usd: z.number().optional(),
  website: z.string().optional(),
  description: z.string().optional(),
  circulating_supply: z.number().optional(),
  max_supply: z.number().optional(),
  total_supply: z.number().optional(),
  fully_diluted_value: z.number().optional(),
  volume_24hr: z.number().optional(),
  volume_24hr_market_cap_pct: z.number().optional(),
  whitepaper: z.string().optional(),
  coin_image_url: z.string().optional(),
  all_time_high: z.number().optional(),
  all_time_low: z.number().optional(),
  fifty_two_week_high: z.number().optional(),
  fifty_two_week_low: z.number().optional(),
  fifty_day_average: z.number().optional(),
  two_hundred_day_average: z.number().optional(),
  average_volume: z.number().optional(),
  previous_close: z.number().optional(),
  previous_close_at: z.string().optional(),
  regular_market_open: z.number().optional(),
  regular_market_price: z.number().optional(),
  regular_market_day_high: z.number().optional(),
  regular_market_day_low: z.number().optional(),
  latest_price: z.number().optional(),
  daily_change_percent: z.number().optional(),
  last_enriched: z.string().optional(),
});

export const fiatAssetSchema = assetCommonSchema.extend({
  type: z.literal("fiat"),
});

export const indexAssetSchema = assetCommonSchema.extend({
  type: z.literal("index"),
  provider: z.string(),
  components: z.array(z.string()),
});

// =============================================================================
// Union Types
// =============================================================================

export const assetSchema = z.discriminatedUnion("type", [
  stockAssetSchema,
  cryptoAssetSchema,
  fiatAssetSchema,
  indexAssetSchema,
  etfAssetSchema,
  fundAssetSchema,
]);

export const buyableAssetSchema = z.discriminatedUnion("type", [
  stockAssetSchema,
  cryptoAssetSchema,
  etfAssetSchema,
  fundAssetSchema,
]);

export type StockAsset = z.infer<typeof stockAssetSchema>;
export type EtfAsset = z.infer<typeof etfAssetSchema>;
export type CryptoAsset = z.infer<typeof cryptoAssetSchema>;
export type FiatAsset = z.infer<typeof fiatAssetSchema>;
export type IndexAsset = z.infer<typeof indexAssetSchema>;
export type FundAsset = z.infer<typeof fundAssetSchema>;
export type Asset = z.infer<typeof assetSchema>;
export type BuyableAsset = z.infer<typeof buyableAssetSchema>;

// =============================================================================
// Chart Schemas
// =============================================================================

export const pricePointSchema = z.object({
  timestamp: z.string(),
  price: z.number(),
});

export const assetChartSchema = z.object({
  chart_id: z.string(),
  asset_id_buy: z.string(),
  asset_id_sell: z.string(),
  update_date: z.string(),
  period: z.string(),
  interval: z.string(),
  price_points: z.array(pricePointSchema),
  last_update: z.string(),
});

export type PricePoint = z.infer<typeof pricePointSchema>;
export type AssetChart = z.infer<typeof assetChartSchema>;

// =============================================================================
// Financial Data Schemas
// =============================================================================

export const statementTypeSchema = z.enum([
  "balance_sheet",
  "income_statement",
  "cashflow",
]);

export const periodTypeSchema = z.enum(["annual", "quarterly"]);

export const financialStatementSchema = z.object({
  asset_id: z.string(),
  statement_type: statementTypeSchema,
  period_type: periodTypeSchema,
  period_end_date: z.string(),
  fiscal_year: z.number().optional(),
  fiscal_quarter: z.string().optional(),
  currency: z.string().optional(),
  metrics: z.record(z.string(), z.union([z.number(), z.string()])),
  created_at: z.string(),
  updated_at: z.string(),
});

export const dividendSchema = z.object({
  asset_id: z.string(),
  date: z.string(),
  amount: z.number(),
  currency: z.string().optional(),
  currency_symbol: z.string().optional(),
});

export const splitSchema = z.object({
  asset_id: z.string(),
  date: z.string(),
  ratio: z.number(),
});

export const earningsEventSchema = z.object({
  date: z.string(),
  eps_estimate: z.number().optional(),
  eps_actual: z.number().optional(),
  revenue_estimate: z.number().optional(),
  revenue_actual: z.number().optional(),
});

export const earningsCalendarSchema = z.object({
  asset_id: z.string(),
  earnings_dates: z.array(earningsEventSchema),
  next_earnings_date: z.string().optional(),
  updated_at: z.string(),
});

export const dividendCalendarSchema = z.object({
  asset_id: z.string(),
  ex_dividend_date: z.string().optional(),
  dividend_date: z.string().optional(),
  forward_dividend_rate: z.number().optional(),
  forward_dividend_yield: z.number().optional(),
  payout_ratio: z.number().optional(),
  five_year_avg_dividend_yield: z.number().optional(),
  trailing_annual_dividend_rate: z.number().optional(),
  trailing_annual_dividend_yield: z.number().optional(),
  updated_at: z.string(),
});

export const recommendationSchema = z.object({
  asset_id: z.string(),
  date: z.string(),
  firm: z.string(),
  to_grade: z.string().optional(),
  from_grade: z.string().optional(),
  action: z.string().optional(),
});

export const newsArticleSchema = z.object({
  asset_id: z.string(),
  uuid: z.string(),
  title: z.string(),
  publisher: z.string(),
  link: z.string(),
  publish_time: z.string(),
  article_type: z.string().optional(),
  thumbnail_url: z.string().optional(),
  related_tickers: z.array(z.string()).optional(),
  created_at: z.string(),
  expires_at: z.string(),
});

export const assetFundamentalsSchema = z.object({
  asset_id: z.string(),
  latest_annual_statement: financialStatementSchema.optional(),
  latest_quarterly_statement: financialStatementSchema.optional(),
  recent_dividends: z.array(dividendSchema).optional(),
  dividend_calendar: dividendCalendarSchema.optional(),
  splits: z.array(splitSchema).optional(),
  earnings_calendar: earningsCalendarSchema.optional(),
  recent_recommendations: z.array(recommendationSchema).optional(),
  news: z.array(newsArticleSchema).optional(),
});

export type FinancialStatement = z.infer<typeof financialStatementSchema>;
export type Dividend = z.infer<typeof dividendSchema>;
export type Split = z.infer<typeof splitSchema>;
export type EarningsEvent = z.infer<typeof earningsEventSchema>;
export type EarningsCalendar = z.infer<typeof earningsCalendarSchema>;
export type DividendCalendar = z.infer<typeof dividendCalendarSchema>;
export type Recommendation = z.infer<typeof recommendationSchema>;
export type NewsArticle = z.infer<typeof newsArticleSchema>;
export type AssetFundamentals = z.infer<typeof assetFundamentalsSchema>;

// =============================================================================
// Company Search Schemas (for company-first asset selection)
// =============================================================================

export const currencyOptionSchema = z.object({
  currency_id: z.string(),
  symbol: z.string(),
  count: z.number(),
});

export const assetListingSchema = z.object({
  asset_id: z.string(),
  symbol: z.string(),
  exchange: z.string(),
  currency_id: z.string(),
  is_primary: z.boolean().optional(),
});

export const companySearchResultSchema = z.object({
  isin: z.string(),
  company_name: z.string(),
  currencies: z.array(currencyOptionSchema),
  listings: z.array(assetListingSchema),
});

export const companySearchResponseSchema = z.object({
  companies: z.array(companySearchResultSchema),
});

export type CurrencyOption = z.infer<typeof currencyOptionSchema>;
export type AssetListing = z.infer<typeof assetListingSchema>;
export type CompanySearchResult = z.infer<typeof companySearchResultSchema>;
export type CompanySearchResponse = z.infer<typeof companySearchResponseSchema>;
